import React, { useEffect, useRef, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import map from "../../assets/Map/gadm36_VNM_1.json";
import { useDispatch, useSelector } from "react-redux";
import "../../style/components/Map.css";
import {
  fetchIngredientDetails,
  fetchIngredientPreview,
} from "../../api/ingredient/ingredient.reduxThunk";
import { useMediaQuery } from "@mui/material";
import { breakpoint } from "../../utils/responsiveUtils";
import { GRAPHICS } from "../../utils/utils";
import {
  setActiveLocation,
  setActiveLocationEmpty,
  setChosenLocationEmpty,
} from "../../api/xoicom.reducer";
import {
  getCenter,
  getScale,
  PROVINCE_GROUPS,
} from "../../utils/ingredientUtils";
import { useNavigate } from "react-router-dom";

const USE_INGREDIENT_SLUG =
  process.env.REACT_APP_USE_INGREDIENT_SLUG === "TRUE";

const vietnam = [map];

const mapRegionStyle = {
  default: {
    outline: "none",
    stroke: "#FFFFFF",
    transition: "all 0.5s ease-in",
  },
  hover: {
    outline: "none",
    stroke: "#FFFFFF",
    transition: "all 0.5s ease-in",
  },
  pressed: {
    outline: "none",
    transition: "all 0.5s ease-in",
  },
};

function Circle() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      className="circle-marker"
    >
      <circle cx="6" cy="6" r="5" fill="black" />
    </svg>
  );
}

function Map({ accentText, language }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const locations = useSelector((state) => state.reducer.ingredientLocations);
  const activeLocation = useSelector((state) => state.reducer.activeLocation);

  const mobile = useMediaQuery(breakpoint.mobile);
  const mobileSm = useMediaQuery(breakpoint.mobileSm);
  const medium = useMediaQuery(breakpoint.medium);
  const large = useMediaQuery(breakpoint.large);
  const xlarge = useMediaQuery(breakpoint.xlarge);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const mapRef = useRef();
  const elementRef = useRef(null);

  useEffect(() => {
    const handleWindowResize = () => setWindowWidth(window.innerWidth);

    handleWindowResize();
    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  const handleHoverMarker = (location) => {
    dispatch(setActiveLocation(location.name));
  };

  const handleClickMarker = (location) => {
    dispatch(setChosenLocationEmpty());
    dispatch(setActiveLocation(location.name));
    getIngredient(location.name);
  };

  const getIngredient = (location) => {
    dispatch(fetchIngredientPreview(location))
      .unwrap()
      .then((ingredients) => {
        if (ingredients.length === 1) {
          getIngredientDetails(ingredients[0]);
        }
      });
  };

  const getIngredientDetails = (ingredient) => {
    if (USE_INGREDIENT_SLUG) {
      navigate(`/ingredient/${ingredient.slug}`);
    } else {
      const { postID } = ingredient;
      dispatch(fetchIngredientDetails(postID[language]))
        .unwrap()
        .then(() => navigate(`/ingredient/${postID[language]}`));
    }
  };

  const scale = getScale(windowWidth, mobile, medium, large, xlarge);
  const center = getCenter(mobile, medium, large, xlarge);

  const getTexTranslate = (locationName) => {
    switch (locationName) {
      case "hai-phong":
        return 12;
      case "ha-noi":
        return 6;
      default:
        return 8;
    }
  };

  return (
    <div ref={elementRef} className="map-controller-container">
      <div>
        <ComposableMap
          ref={mapRef}
          className="map"
          projection="geoMercator"
          projectionConfig={{ scale, center }}
          // coordinate of VietNam [long, lat]
        >
          {vietnam.map((map, index) => (
            <Geographies key={index} geography={map}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const geoKey =
                    geo.properties.VARNAME_1?.toLowerCase().replace(
                      /\s+/g,
                      "-",
                    );
                  const activeGroup = PROVINCE_GROUPS[activeLocation] || [
                    activeLocation,
                  ];
                  const isActive =
                    !!activeLocation && activeGroup.includes(geoKey);
                  return (
                    <Geography
                      style={mapRegionStyle}
                      key={geo.rsmKey}
                      geography={geo}
                      id={geo.rsmKey}
                      fill={isActive ? "#8C062E" : "#EEEAE4"}
                    />
                  );
                })
              }
            </Geographies>
          ))}
          {locations && //if there are no location, map is shown without marker
            locations.map((location) => (
              <Marker
                key={location.name}
                className={`clickable ${location.name}-marker ${
                  !activeLocation
                    ? "display-block"
                    : location.name === activeLocation
                      ? "display-block"
                      : "display-none"
                }`}
                coordinates={location.coordinates}
                // style={{ width: 100 * scale }}
                onMouseEnter={() => handleHoverMarker(location)}
                onMouseLeave={() => dispatch(setActiveLocationEmpty())}
                onClick={() => handleClickMarker(location)}
              >
                <Circle />
                <text
                  x={20}
                  y={getTexTranslate(location.name)}
                  className="body-b2 map-marker-text"
                >
                  {location.display_name}
                </text>
              </Marker>
            ))}
        </ComposableMap>
        {!mobileSm && (
          <div className="map-heading">
            <span className="accent-heading-text">{accentText}</span>
            <img alt="arrow" src={GRAPHICS.ARROW_BUTTON} />
          </div>
        )}
      </div>
    </div>
  );
}

export default Map;
